import { getFinancialHeadersForDebtSchedule } from '../../../../utils/dateUtils';
import dayjs from 'dayjs';
import { convertToFirstLetterCapital, formatRatioValue, safeToNumber } from '../../../../utils/utils';
import { filterFinancialHeadersWithoutClassificationName, isProvisionalHeader } from '../../../../store/investor/constants';
import { deriveAuditedType } from '../../../../modules/Dashboard/DashboardModules/AuditedFinancialsVisualisation/auditedTypeUtils';
import { BANKING_HEADERS, CREDIT_BUREAU_HEADERS } from './dealSummaryEditorConstants';

const AICA_AI_PROFILE_MAIN = 'aicaAIProfileMain';
const AICA_AI_PROFILE_MAIN_V2 = 'aicaAIProfileMainV2';
const AICA_FETCH_ORG_INFO = 'orgInfo';
const POLUS_FETCH_AUDITED_FINANCIAL_BALANCE_SHEET = 'auditedFinancialBalanceSheet';
const POLUS_FETCH_AUDITED_FINANCIAL_PROFIT_AND_LOSS = 'auditedFinancialProfitAndLoss';
const POLUS_FETCH_AUDITED_FINANCIAL_UNSECURED_LOAN = 'auditedFinancialUnsecuredLoan';
const UW_DEBT_SCHEDULE_DATA = 'debtSchedule';
const TOP_5_VENDOR_EXPENSE_LIST = 'top5VendorExpenseList';
const TOP_5_REVENUE_SOURCE_LIST = 'top5RevenueSourceList';
const DOMESTIC_REVENUE_ACROSS_STATES = 'domesticRevenueAcrossStates';
export const COMPANY_METRICS_DATA = 'companyMetricsData';
const FINANCIAL_RATIO_DATA = 'financialRatioData';
const PROVISIONAL_FINANCIAL_RATIO_DATA = 'provisionalFinancialRatioData';

const YELLOW = '#FFD044';
const GenerateError = " Couldn't Generate [Enter Details Here] ";
const DataNotAvl = '[Data Not Available]';

// Default per-column widths (px) for the 2-column label/value tables (Deal
// Summary, Management Profile). Plate stores colSizes as pixel numbers and
// renders <colgroup><col style="width:Npx"> under table-layout: fixed.
// The editor page card is 1229px wide with 96px horizontal padding each side
// (PlateEditor.module.scss: $editor-content-width + .content padding), so the
// table's natural rendered width is 1229 - 96*2 = 1037px. Split true 1:2:
// 1037 / 3 ≈ 345.67 → [346, 691] (sums to 1037).
const DEFAULT_TWO_COL_1_2_SIZES = [346, 691];

// ============================================================================
// Slate Node Helper Functions
// ============================================================================
// These helpers ensure correct Slate node structure:
// - Table cells must contain block elements (paragraphs), not direct text
// - List items must contain block elements (paragraphs), not direct text
// - Lists can only contain list-item children

type TextNode = { text: string; bold?: boolean; bgColor?: string; color?: string; fontSize?: string };

/**
 * Creates a valid table cell with proper paragraph wrapper
 */
const createTableCell = (
    content: string | TextNode | TextNode[],
    options?: { textAlign?: string; type?: 'table-cell' | 'table-header' },
) => {
    const children = Array.isArray(content) ? content : typeof content === 'string' ? [{ text: content }] : [content];

    return {
        type: options?.type ?? 'table-cell',
        children: [
            {
                type: 'paragraph',
                children,
            },
        ],
        ...(options?.textAlign && { textAlign: options.textAlign }),
    };
};

/**
 * Creates a valid list item with proper paragraph wrapper
 */
const createListItem = (content: TextNode | TextNode[]) => {
    const children = Array.isArray(content) ? content : [content];
    return {
        type: 'list-item',
        children: [
            {
                type: 'paragraph',
                children,
            },
        ],
    };
};

const UW_DEBT_SCHEDULE_DATA_HEADERS = ['lenderName', 'typeOfDebtLoan', 'sanctionedAmount', 'outstandingBalance'];

const UW_DEBT_SCHEDULE_DATA_HEADER_NAMES = {
    lenderName: 'Lender Name',
    typeOfDebtLoan: 'Type of Debt/Loan',
    sanctionedAmount: 'Sanctioned Amount (Crs.)',
    outstandingBalance: 'O/S Balance as of Date (Crs.)',
};

// ============================================================================
// One Pager Sections Configuration (per-PNO)
// ============================================================================
// Sections render in `order` ascending; entries with `visible: false` are skipped.
// `label` overrides the section heading. Unknown ids are ignored (defensive).
//
// `gstCustomersVendors` is a sub-section that appears as a row inside the
// Deal Summary table (when its parent section is visible AND the sub-section
// is visible). It is the only sub-section supported today.
//
// When the API returns no config for a PNO, callers should pass `null`/
// `undefined` for `sectionsConfig` and we fall back to `DEFAULT_SECTIONS`.
export type OnePagerSectionId =
    | 'dealSummary'
    | 'managementProfile'
    | 'financialSummary'
    | 'financialRatios'
    | 'debtSchedule'
    | 'creditBureauSummary'
    | 'bankingSummary';

export type OnePagerSubSectionId =
    | 'legalEntityName'
    | 'dateOfIncorporation'
    | 'constitution'
    | 'sectorIndustry'
    | 'headquarters'
    | 'businessOverview'
    | 'previousInvestorsAndFundingStage'
    | 'gstCustomersVendors';

export interface OnePagerSubSectionConfig {
    id: OnePagerSubSectionId;
    visible: boolean;
    label: string;
    order?: number;
}

export interface OnePagerSectionConfig {
    id: OnePagerSectionId;
    visible: boolean;
    label: string;
    order?: number;
    subsections?: OnePagerSubSectionConfig[];
}

const KNOWN_SECTION_IDS: readonly OnePagerSectionId[] = [
    'dealSummary',
    'managementProfile',
    'financialSummary',
    'financialRatios',
    'debtSchedule',
    'creditBureauSummary',
    'bankingSummary',
];

const KNOWN_DEAL_SUMMARY_SUBSECTION_IDS: readonly OnePagerSubSectionId[] = [
    'legalEntityName',
    'dateOfIncorporation',
    'constitution',
    'sectorIndustry',
    'headquarters',
    'businessOverview',
    'previousInvestorsAndFundingStage',
    'gstCustomersVendors',
];

const DEFAULT_DEAL_SUMMARY_SUBSECTIONS: OnePagerSubSectionConfig[] = [
    { id: 'legalEntityName', visible: true, label: 'Legal Entity Name', order: 1 },
    { id: 'dateOfIncorporation', visible: true, label: 'Date of incorporation', order: 2 },
    { id: 'constitution', visible: true, label: 'Constitution', order: 3 },
    { id: 'sectorIndustry', visible: true, label: 'Sector/Industry', order: 4 },
    { id: 'headquarters', visible: true, label: 'Headquarters', order: 5 },
    { id: 'businessOverview', visible: true, label: 'Business Overview', order: 6 },
    { id: 'previousInvestorsAndFundingStage', visible: true, label: 'Previous Investors and Funding Stage', order: 7 },
    { id: 'gstCustomersVendors', visible: true, label: 'GST Customers and Vendors', order: 8 },
];

export const DEFAULT_ONE_PAGER_SECTIONS: OnePagerSectionConfig[] = [
    {
        id: 'dealSummary',
        visible: true,
        label: 'Deal Summary',
        order: 1,
        subsections: DEFAULT_DEAL_SUMMARY_SUBSECTIONS,
    },
    { id: 'managementProfile', visible: true, label: 'Management Profile', order: 2 },
    { id: 'financialSummary', visible: true, label: 'Financial Summary', order: 3 },
    { id: 'financialRatios', visible: true, label: 'Financial Ratios', order: 4 },
    { id: 'debtSchedule', visible: true, label: 'Debt Schedule', order: 5 },
    { id: 'creditBureauSummary', visible: true, label: 'Credit Bureau Summary', order: 6 },
    { id: 'bankingSummary', visible: true, label: 'Banking Summary', order: 7 },
];

/** -----****Initial Data for One Pager Summary****---
 * 1. initialiseDealSummaryEditor : for data initialised using api data.
 * -> 4 children functions to get specific data from the api
 * 2. updatePreferredTermsInEditorData: for data initialised from deal terms step 2 inputs.
 */

export function initialiseDealSummaryEditor(apiData: any, formData: null | Object = {}, sectionsConfig?: OnePagerSectionConfig[] | null) {
    let uw_debt_schedule_data = apiData?.[UW_DEBT_SCHEDULE_DATA] ?? null;
    let ai_profile_main_data = apiData?.[AICA_AI_PROFILE_MAIN] ?? null;
    let ai_profile_main_data_v2 = apiData?.[AICA_AI_PROFILE_MAIN_V2] ?? null;
    let audited_financial_profit_and_loss = apiData?.[POLUS_FETCH_AUDITED_FINANCIAL_PROFIT_AND_LOSS] ?? null;
    let audited_financial_balance_sheet = apiData?.[POLUS_FETCH_AUDITED_FINANCIAL_BALANCE_SHEET] ?? null;
    let audited_financial_unsecured_loan = processAuditedFinancialUnsecuredLoanData(
        apiData?.[POLUS_FETCH_AUDITED_FINANCIAL_UNSECURED_LOAN] ?? null,
    );
    let orgInfo = apiData?.[AICA_FETCH_ORG_INFO] ?? null;
    let companyMetricsData = apiData?.[COMPANY_METRICS_DATA]?.borrowerFinancials ?? {};
    let companyMetricsLastUpdatedDate =
        companyMetricsData?.lastUpdatedAt && dayjs(companyMetricsData?.lastUpdatedAt).isValid()
            ? dayjs(companyMetricsData?.lastUpdatedAt).format('DD MMM YYYY')
            : null;

    let topFiveVendorExpensePlots = apiData?.[TOP_5_VENDOR_EXPENSE_LIST] ?? [];
    let topFiveRevenueSourcePlots = apiData?.[TOP_5_REVENUE_SOURCE_LIST] ?? [];
    let domesticRevenueAcrossStates = apiData?.[DOMESTIC_REVENUE_ACROSS_STATES] ?? null;
    let financialRatioData = apiData?.[FINANCIAL_RATIO_DATA] ?? null;
    let provisionalFinancialRatioData = apiData?.[PROVISIONAL_FINANCIAL_RATIO_DATA] ?? {};

    // Read pre-formatted director and banking data from root level. The Polus Unifier
    // route `getAicaOnePagerData` serves `directorBureauList` already in display-ready
    // shape via `transformOnePagerDirectorOverview`, and `bankingOverviewList` via
    // `transformOnePagerBankingOverview`. Any name-matching / sort across multiple
    // upstream sources (e.g. merging with onboarding management list) is the BE's
    // responsibility — the FE does no client-side reconciliation here.
    let combinedDirectors = apiData?.directorBureauList ?? [];
    let bankingOverviewList = apiData?.bankingOverviewList ?? [];
    let encumberedCashBalance = companyMetricsData?.encumberedCashBalance ?? null;

    // GST top-5 lists arrive pre-formatted from Polus Groovy:
    //   [{ name: "VendorName", amountDisplay: "(₹X.XX Lakh)" }, ...]
    // (Transform: transformOnePagerGstVendorList / transformOnePagerGstRevenueList)
    const topFiveVendorExpense: Array<{ name: string; amountDisplay: string }> = Array.isArray(topFiveVendorExpensePlots)
        ? topFiveVendorExpensePlots
        : [];
    const topFiveRevenueSource: Array<{ name: string; amountDisplay: string }> = Array.isArray(topFiveRevenueSourcePlots)
        ? topFiveRevenueSourcePlots
        : [];

    let uw_debt_schedule_data_headers =
        uw_debt_schedule_data?.headers?.filter((header) => UW_DEBT_SCHEDULE_DATA_HEADERS?.includes(header?.key)) ?? [];
    let table_uwDebtSchedule: any = null,
        table_managementProfile: any = null,
        table_dealSummary: any = null,
        table_preferredDealTerms: any = null,
        table_financialSummary: any = null;

    function getDealSummaryData(subsectionsConfig: OnePagerSubSectionConfig[] | null) {
        // Row builders — one per sub-section id. Each takes the configured label
        // and returns a Slate table-row. Keeps data extraction in closure.
        const buildLegalEntityName = (label: string): any => ({
            type: 'table-row',
            children: [
                {
                    type: 'table-cell',
                    children: [{ type: 'paragraph', children: [{ text: label }] }],
                },
                {
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [
                                orgInfo?.orgName
                                    ? { text: `${orgInfo?.orgName ?? ''}`, bold: true }
                                    : { text: ` ${DataNotAvl}`, bgColor: YELLOW, bold: true },
                            ],
                        },
                    ],
                },
            ],
        });

        const buildDateOfIncorporation = (label: string): any => ({
            type: 'table-row',
            children: [
                { type: 'table-cell', children: [{ type: 'paragraph', children: [{ text: label }] }] },
                {
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [
                                ai_profile_main_data?.foundationDate
                                    ? { text: `${ai_profile_main_data?.foundationDate ?? ''}`, bold: true }
                                    : { text: `${DataNotAvl}`, bgColor: YELLOW, bold: true },
                            ],
                        },
                    ],
                },
            ],
        });

        const buildConstitution = (label: string): any => ({
            type: 'table-row',
            children: [
                { type: 'table-cell', children: [{ type: 'paragraph', children: [{ text: label }] }] },
                {
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [
                                orgInfo?.constitutionType
                                    ? { text: `${orgInfo?.constitutionType ?? ''}`, bold: true }
                                    : { text: `${DataNotAvl}`, bgColor: YELLOW, bold: true },
                            ],
                        },
                    ],
                },
            ],
        });

        const buildSectorIndustry = (label: string): any => ({
            key: 'sector_industry',
            type: 'table-row',
            children: [
                { type: 'table-cell', children: [{ type: 'paragraph', children: [{ text: label }] }] },
                {
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [
                                formData?.['borrowerProfile.companySector'] || ai_profile_main_data_v2?.data?.sectorDetails?.sector
                                    ? {
                                          text: `${
                                              formData?.['borrowerProfile.companySector']
                                                  ? formData?.['borrowerProfile.companySector']
                                                  : ai_profile_main_data_v2.data.sectorDetails.sector
                                          }`,
                                          bold: true,
                                      }
                                    : { text: `${DataNotAvl}`, bgColor: YELLOW, bold: true },
                            ],
                        },
                    ],
                },
            ],
        });

        const buildHeadquarters = (label: string): any => ({
            type: 'table-row',
            children: [
                { type: 'table-cell', children: [{ type: 'paragraph', children: [{ text: label }] }] },
                {
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [
                                ai_profile_main_data?.location
                                    ? { text: `${ai_profile_main_data?.location ?? ''}`, bold: true }
                                    : { text: `${DataNotAvl}`, bgColor: YELLOW, bold: true },
                            ],
                        },
                    ],
                },
            ],
        });

        const buildBusinessOverview = (label: string): any => ({
            type: 'table-row',
            children: [
                { type: 'table-cell', children: [{ type: 'paragraph', children: [{ text: label }] }] },
                {
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [
                                ai_profile_main_data_v2?.data?.overview?.overviewContent
                                    ? { text: ai_profile_main_data_v2.data.overview.overviewContent }
                                    : { text: `${DataNotAvl}`, bgColor: YELLOW },
                            ],
                        },
                    ],
                },
            ],
        });

        const buildPreviousInvestorsAndFundingStage = (label: string): any => ({
            type: 'table-row',
            children: [
                { type: 'table-cell', children: [{ type: 'paragraph', children: [{ text: label }] }] },
                {
                    type: 'table-cell',
                    children: [
                        {
                            type: 'unorderedList',
                            children: [
                                createListItem([
                                    { text: `Investor Name(s): `, bold: true },
                                    ai_profile_main_data_v2?.data?.overview?.keyInvestors?.length
                                        ? {
                                              text: ai_profile_main_data_v2.data.overview.keyInvestors
                                                  .map((investor: any) => investor)
                                                  .join(', '),
                                          }
                                        : { text: `${DataNotAvl}`, bgColor: YELLOW },
                                ]),
                                createListItem([
                                    { text: `Funding Stage: `, bold: true },
                                    ai_profile_main_data?.fundingInfo?.stage
                                        ? { text: `${ai_profile_main_data?.fundingInfo?.stage ?? ''}` }
                                        : { text: `${DataNotAvl}`, bgColor: YELLOW },
                                ]),
                            ],
                        },
                    ],
                },
            ],
        });

        const buildGstCustomersVendors = (label: string): any => ({
            type: 'table-row',
            children: [
                createTableCell(label),
                {
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [
                                { text: 'Top 5 Customers ', bold: true },
                                { text: '(revenue in last 12 months)', color: '#9D9D9D' },
                            ],
                        },
                        {
                            type: 'unorderedList',
                            children: topFiveRevenueSource?.length
                                ? topFiveRevenueSource.map((item) =>
                                      createListItem([{ text: `${item.name}  ` }, { text: item.amountDisplay, color: '#9D9D9D' }]),
                                  )
                                : [createListItem([{ text: DataNotAvl, bgColor: YELLOW }])],
                        },
                        { type: 'paragraph', children: [{ text: '' }] },
                        {
                            type: 'paragraph',
                            children: [
                                { text: 'Top 5 Vendors ', bold: true },
                                { text: '(expense in last 12 months)', color: '#9D9D9D' },
                            ],
                        },
                        {
                            type: 'unorderedList',
                            children: topFiveVendorExpense?.length
                                ? topFiveVendorExpense.map((item) =>
                                      createListItem([{ text: `${item.name}  ` }, { text: item.amountDisplay, color: '#9D9D9D' }]),
                                  )
                                : [createListItem([{ text: DataNotAvl, bgColor: YELLOW }])],
                        },
                    ],
                },
            ],
        });

        const ROW_BUILDERS: Record<OnePagerSubSectionId, (label: string) => any> = {
            legalEntityName: buildLegalEntityName,
            dateOfIncorporation: buildDateOfIncorporation,
            constitution: buildConstitution,
            sectorIndustry: buildSectorIndustry,
            headquarters: buildHeadquarters,
            businessOverview: buildBusinessOverview,
            previousInvestorsAndFundingStage: buildPreviousInvestorsAndFundingStage,
            gstCustomersVendors: buildGstCustomersVendors,
        };

        // Resolve the row list. Null/empty → default order with default labels.
        const resolvedSubsections =
            Array.isArray(subsectionsConfig) && subsectionsConfig.length ? subsectionsConfig : DEFAULT_DEAL_SUMMARY_SUBSECTIONS;

        const orderedRows = resolvedSubsections
            .filter((s) => s?.visible && KNOWN_DEAL_SUMMARY_SUBSECTION_IDS.includes(s.id))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        const builtRows = orderedRows.map((sub) => ROW_BUILDERS[sub.id](sub.label || sub.id));

        const dealSummary: any = {
            key: 'table_dealSummary',
            type: 'tableWithoutHeaders',
            columns: 2,
            rows: builtRows.length,
            colSizes: DEFAULT_TWO_COL_1_2_SIZES,
            children: builtRows,
        };

        return dealSummary;
    }
    function getManagementProfileData() {
        let managementProfileData = {
            key: 'table_managementProfile',
            type: 'tableWithoutHeaders',
            columns: 2,
            rows: 0,
            colSizes: DEFAULT_TWO_COL_1_2_SIZES,
            children: !!ai_profile_main_data?.directors?.length
                ? ai_profile_main_data?.directors?.map((director: any, index) => ({
                      type: 'table-row',
                      children: [
                          {
                              type: 'table-cell',
                              children: [
                                  {
                                      type: 'paragraph',
                                      children: [{ text: director?.name ?? '' }],
                                  },
                              ],
                          },
                          {
                              type: 'table-cell',
                              children: [
                                  {
                                      type: 'unorderedList',
                                      children: [
                                          createListItem([
                                              {
                                                  text: `Designation: `,
                                                  bold: true,
                                              },
                                              director?.designation
                                                  ? {
                                                        text: `${director?.designation ?? ''}`,
                                                    }
                                                  : {
                                                        text: DataNotAvl,
                                                        bgColor: YELLOW,
                                                        bold: true,
                                                    },
                                          ]),
                                          createListItem([
                                              {
                                                  text: `Director Since:`,
                                                  bold: true,
                                              },
                                              director?.tenureBeginDate
                                                  ? {
                                                        text: ` ${dayjs(director?.tenureBeginDate).format('MMM YYYY')}`,
                                                    }
                                                  : {
                                                        text: DataNotAvl,
                                                        bgColor: YELLOW,
                                                        bold: true,
                                                    },
                                          ]),
                                          createListItem([
                                              { text: `Age:`, bold: true },
                                              director?.age
                                                  ? {
                                                        text: ` ${director?.age ?? ''}`,
                                                    }
                                                  : {
                                                        text: DataNotAvl,
                                                        bgColor: YELLOW,
                                                        bold: true,
                                                    },
                                          ]),
                                      ],
                                  },
                              ],
                          },
                      ],
                  }))
                : [
                      {
                          type: 'table-row',
                          children: [
                              {
                                  type: 'table-cell',
                                  children: [
                                      {
                                          type: 'paragraph',
                                          children: [{ text: DataNotAvl, bgColor: YELLOW }],
                                      },
                                  ],
                              },
                              {
                                  type: 'table-cell',
                                  children: [
                                      {
                                          type: 'paragraph',
                                          children: [{ text: DataNotAvl, bgColor: YELLOW }],
                                      },
                                  ],
                              },
                          ],
                      },
                  ],
        };
        managementProfileData.rows = managementProfileData?.children?.length ?? 0;
        return managementProfileData;
    }
    function getDebtScheduleData() {
        let uwDebtSchedule = {
            key: 'table_uwDebtSchedule',
            type: 'table',
            custom: true,
            columns: uw_debt_schedule_data_headers?.length || UW_DEBT_SCHEDULE_DATA_HEADERS?.length,
            rows: 0,
            children: [
                {
                    type: 'table-row',
                    children: uw_debt_schedule_data_headers?.length
                        ? uw_debt_schedule_data_headers?.map?.((header, index) => ({
                              type: 'table-header',
                              children: [
                                  {
                                      type: 'paragraph',
                                      children: [
                                          {
                                              text: header?.name ?? '',
                                              bold: true,
                                          },
                                      ],
                                  },
                              ],
                              ...(index > 1 && { textAlign: 'right' }),
                          }))
                        : UW_DEBT_SCHEDULE_DATA_HEADERS.map((headerKey, index) => ({
                              type: 'table-header',
                              children: [
                                  {
                                      type: 'paragraph',
                                      children: [
                                          {
                                              text: UW_DEBT_SCHEDULE_DATA_HEADER_NAMES?.[headerKey] ?? '',
                                              bold: true,
                                          },
                                      ],
                                  },
                              ],
                              ...(index > 1 && { textAlign: 'right' }),
                          })),
                },
                ...(!!uw_debt_schedule_data?.rows?.length && !!uw_debt_schedule_data_headers?.length
                    ? uw_debt_schedule_data?.rows?.map((row: any) => {
                          return {
                              type: 'table-row',
                              children: uw_debt_schedule_data_headers?.map?.((header, index) => ({
                                  type: 'table-cell',
                                  children: [
                                      {
                                          type: 'paragraph',
                                          children: [{ text: `${row?.[header?.key] ?? ''}` }],
                                      },
                                  ],
                                  ...(index > 1 && { textAlign: 'right' }),
                              })),
                          };
                      })
                    : [
                          {
                              type: 'table-row',
                              children: UW_DEBT_SCHEDULE_DATA_HEADERS?.map((headerKey, index) => ({
                                  type: 'table-cell',
                                  children: [
                                      {
                                          type: 'paragraph',
                                          children: [{ text: DataNotAvl, bgColor: YELLOW }],
                                      },
                                  ],
                                  ...(index > 1 && { textAlign: 'right' }),
                              })),
                          },
                      ]),
                ...(uw_debt_schedule_data?.rows?.length &&
                ('totalSanctionedAmount' in uw_debt_schedule_data || 'totalOutstandingAmount' in uw_debt_schedule_data)
                    ? [
                          {
                              type: 'table-row',
                              children: [
                                  {
                                      type: 'table-cell',
                                      children: [
                                          {
                                              type: 'paragraph',
                                              children: [{ text: 'Total' }],
                                          },
                                      ],
                                  },
                                  {
                                      type: 'table-cell',
                                      children: [
                                          {
                                              type: 'paragraph',
                                              children: [{ text: '' }],
                                          },
                                      ],
                                  },
                                  {
                                      type: 'table-cell',
                                      children: [
                                          {
                                              type: 'paragraph',
                                              children: [
                                                  {
                                                      text: `${uw_debt_schedule_data?.totalSanctionedAmount ?? '-'}`,
                                                  },
                                              ],
                                          },
                                      ],
                                      textAlign: 'right',
                                  },
                                  {
                                      type: 'table-cell',
                                      children: [
                                          {
                                              type: 'paragraph',
                                              children: [
                                                  {
                                                      text: `${uw_debt_schedule_data?.totalOutstandingAmount ?? '-'}`,
                                                  },
                                              ],
                                          },
                                      ],
                                      textAlign: 'right',
                                  },
                              ],
                          },
                      ]
                    : []),
            ],
        };
        uwDebtSchedule.rows = uwDebtSchedule?.children?.length;
        return uwDebtSchedule;
    }
    function getFinancialSummary() {
        const financialRows = [
            { key: 'Revenue', companyFinancialsKey: 'annualRevenue', label: 'Revenue', table: 'pnl' },
            { key: 'EBITDA', companyFinancialsKey: 'ebitda', label: 'EBITDA', table: 'pnl' },
            { key: 'Profit After Tax', companyFinancialsKey: 'pat', label: 'Profit After Tax', table: 'pnl' },
            { key: 'empty-row', label: '', table: '' },
            { key: 'Net Worth', companyFinancialsKey: 'netWorth', label: 'Net Worth', table: 'balanceSheet' },
            { key: 'Long Term Borrowings', companyFinancialsKey: 'longTermDebt', label: 'Long Term Debt', table: 'unsecuredLoans' },
            { key: 'Short Term Borrowings', companyFinancialsKey: 'shortTermDebt', label: 'Short Term Debt', table: 'unsecuredLoans' },
            {
                key: 'Cash And Cash Equivalents',
                companyFinancialsKey: 'cashAndCashEquivalents',
                label: 'Cash And Cash Equivalents',
                table: 'balanceSheet',
            },
            { key: 'Net Working Capital', companyFinancialsKey: 'workingCapitalGap', label: 'Net Working Capital', table: 'balanceSheet' },
            { key: 'Trade Receivables', companyFinancialsKey: 'tradeReceivables', label: '\tTrade Receivables', table: 'balanceSheet' },
            { key: 'Inventories', companyFinancialsKey: 'inventory', label: '\tInventory', table: 'balanceSheet' },
            { key: 'Trade Payables', companyFinancialsKey: 'tradePayables', label: '\tTrade Payables', table: 'balanceSheet' },
            { key: 'Total Assets', companyFinancialsKey: 'totalAssets', label: 'Total Balance Sheet Size', table: 'balanceSheet' },
        ];

        const FINANCIAL_HEADERS = getFinancialHeadersForDebtSchedule(companyMetricsLastUpdatedDate);

        let table_financialSummary: any = null;
        table_financialSummary = {
            key: 'table_financialSummary',
            type: 'table',
            custom: true,
            columns: FINANCIAL_HEADERS?.length ?? 0,
            rows: 0,
            children: [
                {
                    type: 'table-row',
                    children: FINANCIAL_HEADERS?.map((header, index) => ({
                        type: 'table-header',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: `${header?.name ?? '-'}` }],
                            },
                        ],
                        ...(index > 0 && { textAlign: 'right' }),
                    })),
                },
            ],
        };

        financialRows?.forEach((financialRow) => {
            const isEmptyRow = financialRow?.key === 'empty-row';
            let row: any = {
                type: 'table-row',
            };

            let data: any;
            switch (financialRow?.table) {
                case 'pnl':
                    data = audited_financial_profit_and_loss?.[financialRow?.key];
                    break;
                case 'unsecuredLoans':
                    data = audited_financial_unsecured_loan?.rows?.find((row: any) => row?.parent === financialRow?.key);
                    break;
                case 'balanceSheet':
                    data = audited_financial_balance_sheet?.[financialRow?.key];
                    break;
            }

            row.children = [
                {
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [{ text: financialRow?.label }],
                        },
                    ],
                },
            ];
            FINANCIAL_HEADERS?.filter?.(filterFinancialHeadersWithoutClassificationName)?.forEach((header) => {
                const isProvisionalHeaderValue = isProvisionalHeader(header);
                const isCashAndCashEquivalentsRow = financialRow?.key === 'Cash And Cash Equivalents';

                const getText = () => {
                    let companyMetricsValue;
                    if (isProvisionalHeaderValue) {
                        // For Cash And Cash Equivalents in Provisional, calculate as encumberedCashBalance + cashBalance
                        if (isCashAndCashEquivalentsRow) {
                            const encumberedCash = safeToNumber(companyMetricsData?.encumberedCashBalance);
                            const cashBalance = safeToNumber(companyMetricsData?.cashBalance);
                            const totalCash = encumberedCash + cashBalance;
                            companyMetricsValue = formatCompanyMetricsValue(totalCash);
                            return `${companyMetricsValue}`;
                        }
                        companyMetricsValue = formatCompanyMetricsValue(companyMetricsData?.[financialRow?.companyFinancialsKey as string]);
                        return `${companyMetricsValue}`;
                    }

                    if (isEmptyRow) return '';
                    return `${data?.[header?.key] ?? '-'}`;
                };

                row.children.push({
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [{ text: getText() }],
                        },
                    ],
                    textAlign: 'right',
                });
            });

            table_financialSummary.children.push(row);
        });
        table_financialSummary.rows = table_financialSummary?.children?.length ?? 0;
        return table_financialSummary;
    }

    function getFinancialRatios() {
        if (!financialRatioData || !financialRatioData.headers || !financialRatioData.metrics) {
            return null;
        }

        const ratioMetrics = financialRatioData.metrics;

        // Use Financial Summary headers format for consistency (includes provisional column)
        const FINANCIAL_HEADERS = getFinancialHeadersForDebtSchedule(companyMetricsLastUpdatedDate);

        // Define the ratios to display in order
        const ratioRows = [
            { metricKey: 'Days Payables Outstanding (DPO)', label: 'Days Payables Outstanding (DPO)' },
            { metricKey: 'Days Inventory Outstanding (DIO)', label: 'Days Inventory Outstanding (DIO)' },
            { metricKey: 'Days Sales Outstanding (DSO)', label: 'Days Sales Outstanding (DSO)' },
            { metricKey: 'Cash Conversion Cycle (Days)', label: 'Cash Conversion Cycle (Days)' },
            { metricKey: 'Current Ratio', label: 'Current Ratio' },
            { metricKey: 'Debt to equity', label: 'Debt / Equity' },
            { metricKey: 'Debt / EBITDA', label: 'Debt / EBITDA' },
            { metricKey: 'TOL / TNW', label: 'TOL / TNW' },
            { metricKey: 'DSCR', label: 'Debt Service Coverage Ratio (DSCR)' },
        ];

        // Map companyMetricsData keys to ratio metric keys for provisional data
        const provisionalKeyMap: Record<string, string> = {
            'Days Payables Outstanding (DPO)': 'daysPayablesOutStanding',
            'Days Inventory Outstanding (DIO)': 'daysInventoryOutStanding',
            'Days Sales Outstanding (DSO)': 'daysSalesOutStanding',
            'Cash Conversion Cycle (Days)': 'CashConversionCycleDays',
            'Current Ratio': 'CurrentRatio',
            'Debt to equity': 'DebtToEquity',
            'Debt / EBITDA': 'DebtToEBIDTA',
            'TOL / TNW': 'TOLByTNW',
            DSCR: 'DSCR',
        };

        // Ingore these keys for decimal formatting since they are already formatted as days (integers) from the backend and should not be rounded further
        const daysMetricKeys = new Set([
            'Days Payables Outstanding (DPO)',
            'Days Inventory Outstanding (DIO)',
            'Days Sales Outstanding (DSO)',
            'Cash Conversion Cycle (Days)',
        ]);

        let table_financialRatios: any = {
            key: 'table_financialRatios',
            type: 'table',
            custom: true,
            columns: FINANCIAL_HEADERS?.length ?? 0,
            rows: 0,
            children: [
                {
                    type: 'table-row',
                    children: FINANCIAL_HEADERS?.map((header, index) => ({
                        type: 'table-header',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: `${header?.name ?? '-'}` }],
                            },
                        ],
                        ...(index > 0 && { textAlign: 'right' }),
                    })),
                },
            ],
        };

        ratioRows?.forEach((ratioRow) => {
            const metricData = ratioMetrics[ratioRow.metricKey];
            if (!metricData) return;

            const isDaysMetric = daysMetricKeys.has(ratioRow.metricKey);

            const row: any = {
                type: 'table-row',
            };

            row.children = [
                {
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [{ text: ratioRow.label }],
                        },
                    ],
                },
            ];

            // Process each header (skip the first one which is "Particulars (Cr)")
            FINANCIAL_HEADERS?.filter?.(filterFinancialHeadersWithoutClassificationName)?.forEach((header) => {
                const isProvisionalHeaderValue = isProvisionalHeader(header);
                let cellValue = '-';

                if (isProvisionalHeaderValue) {
                    // Get provisional value from companyMetricsData
                    const provisionalKey = provisionalKeyMap[ratioRow.metricKey];
                    const provisionalValue = provisionalFinancialRatioData?.[provisionalKey];
                    if (provisionalKey && provisionalValue != null) {
                        cellValue = formatRatioValue(provisionalValue, metricData?.unit || '', false, isDaysMetric ? 0 : undefined);
                    }
                } else {
                    // Get audited year value from metric data using year key
                    const yearValue = metricData?.[header?.key];
                    if (yearValue !== null && yearValue !== undefined) {
                        cellValue = formatRatioValue(
                            yearValue,
                            metricData?.unit || '',
                            metricData?.roundOffRequired || false,
                            isDaysMetric ? 0 : undefined,
                        );
                    }
                }

                row.children.push({
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [{ text: cellValue }],
                        },
                    ],
                    textAlign: 'right',
                });
            });

            table_financialRatios.children.push(row);
        });

        table_financialRatios.rows = table_financialRatios?.children?.length ?? 0;
        return table_financialRatios;
    }

    function getCreditBureauSummary() {
        if (!combinedDirectors?.length) return null;

        // Display fields are pre-formatted by Polus Groovy `transformOnePagerDirectorOverview`.
        // Header keys + labels live in `dealSummaryEditorConstants.ts`.

        let creditBureauTable: any = {
            key: 'table_creditBureauSummary',
            type: 'table',
            custom: true,
            columns: CREDIT_BUREAU_HEADERS.length,
            rows: 0,
            children: [
                {
                    type: 'table-row',
                    children: CREDIT_BUREAU_HEADERS.map((header, index) => ({
                        type: 'table-header',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: header.label, bold: true }],
                            },
                        ],
                        ...(index > 0 && { textAlign: 'right' }),
                    })),
                },
                ...combinedDirectors.map((director: any) => ({
                    type: 'table-row',
                    children: CREDIT_BUREAU_HEADERS.map((header, index) => ({
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [
                                    {
                                        // Defensive fallback to raw `name` in case the BE ever ships a director
                                        // record without the pre-formatted `nameDisplay` field.
                                        text:
                                            header.key === 'nameDisplay'
                                                ? director?.nameDisplay ?? director?.name ?? '-'
                                                : director?.[header.key] ?? '-',
                                    },
                                ],
                            },
                        ],
                        ...(index > 0 && { textAlign: 'right' }),
                    })),
                })),
            ],
        };
        creditBureauTable.rows = creditBureauTable.children.length;
        return creditBureauTable;
    }

    function getBankingSummary() {
        if (!bankingOverviewList?.length) return null;

        // All display strings (incl. totals-row account count) are pre-formatted by
        // Polus Groovy `transformOnePagerBankingOverview`. We only style the row.
        // Header keys + labels live in `dealSummaryEditorConstants.ts`.

        let bankingTable: any = {
            key: 'table_bankingSummary',
            type: 'table',
            custom: true,
            columns: BANKING_HEADERS.length,
            rows: 0,
            children: [
                {
                    type: 'table-row',
                    children: BANKING_HEADERS.map((header, index) => ({
                        type: 'table-header',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: header.label, bold: true }],
                            },
                        ],
                        ...(index > 1 && { textAlign: 'right' }),
                    })),
                },
                ...bankingOverviewList.map((bank: any) => {
                    const totalsRow = !!bank?.isTotalsRow;
                    const cellType = totalsRow ? 'table-header' : 'table-cell';
                    return {
                        type: 'table-row',
                        children: BANKING_HEADERS.map((header, index) => ({
                            type: cellType,
                            children: [
                                {
                                    type: 'paragraph',
                                    children: [{ text: bank?.[header.key] ?? '-', ...(totalsRow && { bold: true }) }],
                                },
                            ],
                            ...(index > 1 && { textAlign: 'right' }),
                        })),
                    };
                }),
            ],
        };
        bankingTable.rows = bankingTable.children.length;
        return bankingTable;
    }

    // Resolve the section list. Null/empty config → default layout (all sections visible).
    const resolvedSections = Array.isArray(sectionsConfig) && sectionsConfig.length ? sectionsConfig : DEFAULT_ONE_PAGER_SECTIONS;

    // Filter to visible & known ids, then sort by `order` (missing order → 0 → preserves
    // insertion order via stable sort).
    const visibleSections = resolvedSections
        .filter((s) => s?.visible && KNOWN_SECTION_IDS.includes(s.id))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // Derived suffixes — preserved from the legacy layout.
    const auditedSuffix = (() => {
        const derived = deriveAuditedType(
            audited_financial_balance_sheet?.latestConsolidatedFY ?? null,
            audited_financial_balance_sheet?.latestStandaloneFY ?? null,
        );
        return derived ? ` - ${derived}` : '';
    })();
    const debtScheduleSuffix = uw_debt_schedule_data?.asOfDate
        ? ` (as of ${dayjs(uw_debt_schedule_data.asOfDate).format("MMM'YY")})`
        : companyMetricsLastUpdatedDate
        ? ` (as of ${dayjs(companyMetricsData?.lastUpdatedAt).format("MMM'YY")})`
        : '';

    const headingTwo = (text: string) => ({ type: 'headingTwo', children: [{ text }] });
    const spacer = () => ({ type: 'paragraph', children: [{ text: '\n' }] });

    let updatedData: any[] = [];

    for (const section of visibleSections) {
        switch (section.id) {
            case 'dealSummary': {
                // Pass the full subsections list (or null → use defaults inside the builder).
                const table = getDealSummaryData(section.subsections ?? null);
                if (table && table.children?.length) {
                    updatedData.push(headingTwo(section.label));
                    updatedData.push(table);
                    updatedData.push(spacer());
                }
                break;
            }
            case 'managementProfile': {
                const table = getManagementProfileData();
                if (table) {
                    updatedData.push(headingTwo(section.label));
                    updatedData.push(table);
                    updatedData.push(spacer());
                }
                break;
            }
            case 'financialSummary': {
                const table = getFinancialSummary();
                if (table) {
                    updatedData.push(headingTwo(`${section.label}${auditedSuffix}`));
                    updatedData.push(table);
                    if (encumberedCashBalance) {
                        updatedData.push({
                            type: 'paragraph',
                            children: [
                                {
                                    text: `** Out of the total cash and cash equivalents, encumbered cash balance is ${formatCompanyMetricsValue(
                                        encumberedCashBalance,
                                    )} Cr`,
                                    color: '#9D9D9D',
                                    fontSize: '14px',
                                },
                            ],
                        });
                    }
                    updatedData.push({
                        type: 'paragraph',
                        children: [
                            {
                                text: `\n*Note: Provisional metrics are updated ${
                                    companyMetricsLastUpdatedDate ? 'as of ' + companyMetricsLastUpdatedDate : ''
                                } basis unaudited data.`,
                                color: '#9D9D9D',
                                fontSize: '14px',
                            },
                        ],
                    });
                    updatedData.push(spacer());
                }
                break;
            }
            case 'financialRatios': {
                const table = getFinancialRatios();
                if (table) {
                    updatedData.push(headingTwo(`${section.label}${auditedSuffix}`));
                    updatedData.push(table);
                    updatedData.push(spacer());
                }
                break;
            }
            case 'debtSchedule': {
                const table = getDebtScheduleData();
                if (table) {
                    updatedData.push(headingTwo(`${section.label}${debtScheduleSuffix}`));
                    updatedData.push(table);
                    updatedData.push(spacer());
                }
                break;
            }
            case 'creditBureauSummary': {
                const table = getCreditBureauSummary();
                if (table) {
                    updatedData.push(headingTwo(section.label));
                    updatedData.push(table);
                    updatedData.push(spacer());
                }
                break;
            }
            case 'bankingSummary': {
                const table = getBankingSummary();
                if (table) {
                    updatedData.push(headingTwo(section.label));
                    updatedData.push(table);
                    updatedData.push(spacer());
                }
                break;
            }
            default:
                // Unknown id — defensive, already filtered above. Skip.
                break;
        }
    }
    // if (formData) {
    //     table_preferredDealTerms = updatePreferredTermsInEditorData(formData);
    //     if (table_preferredDealTerms) {
    //         updatedData = [
    //             ...updatedData,
    //             { type: 'headingThree', children: [{ text: 'Preferred Terms' }] },
    //             table_preferredDealTerms,
    //         ];
    //     }
    // }
    updatedData = [
        ...updatedData,
        {
            type: 'paragraph',
            children: [{ text: '\n' }],
        },
        // {
        //     type: 'image',
        //     url: poweredByAICA,
        //     children: [{ text: '' }],
        //     height: '20px',
        //     justify: 'center',
        // },
    ];
    return updatedData;

    // setTimeout(() => {
    //     setGenerating(false);
    //     setEditorReady(true);
    // }, 200);
}

export const updatePreferredTermsInEditorData = (formData: any) => {
    let table_preferredDealTerms: any = null;

    table_preferredDealTerms = {
        key: 'table_preferredDealTerms',
        type: 'tableWithoutHeaders',
        columns: 2,
        rows: 0,
        children: [
            {
                type: 'table-row',
                children: [
                    {
                        type: 'table-header',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Amount' }],
                            },
                        ],
                    },
                    {
                        type: 'table-header',
                        children: [
                            {
                                type: 'paragraph',
                                children: [
                                    formData?.['dealTerms.maximumDealAmount']
                                        ? { text: `${formData?.['dealTerms.maximumDealAmount']}` }
                                        : { text: DataNotAvl, bgColor: YELLOW },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                type: 'table-row',
                children: [
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Rate of Interest' }],
                            },
                        ],
                    },
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [
                                    formData?.['dealTerms.minimumRoi'] && formData?.['dealTerms.maximumRoi']
                                        ? {
                                              text:
                                                  formData?.['dealTerms.minimumRoi'] && formData?.['dealTerms.maximumRoi']
                                                      ? `${formData['dealTerms.minimumRoi']} - ${formData['dealTerms.maximumRoi']}%`
                                                      : '',
                                          }
                                        : { text: DataNotAvl, bgColor: YELLOW },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                type: 'table-row',
                children: [
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Product' }],
                            },
                        ],
                    },
                    {
                        type: 'table-cell',
                        children: [
                            formData?.['dealTerms.loanTypes']?.length
                                ? {
                                      type: 'unorderedList',
                                      children: formData?.['dealTerms.loanTypes']?.map((type: any) =>
                                          createListItem([{ text: `${type ?? '-'}` }]),
                                      ),
                                  }
                                : {
                                      type: 'paragraph',
                                      children: [{ text: DataNotAvl, bgColor: YELLOW }],
                                  },
                        ],
                    },
                ],
            },
            {
                type: 'table-row',
                children: [
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Tenure(months)' }],
                            },
                        ],
                    },
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [
                                    formData?.['dealTerms.minimumTenure'] && formData?.['dealTerms.maximumTenure']
                                        ? {
                                              text:
                                                  formData?.['dealTerms.minimumTenure'] && formData?.['dealTerms.maximumTenure']
                                                      ? `${formData['dealTerms.minimumTenure']} - ${formData['dealTerms.maximumTenure']} months`
                                                      : '',
                                          }
                                        : { text: DataNotAvl, bgColor: YELLOW },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                type: 'table-row',
                children: [
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Security' }],
                            },
                        ],
                    },
                    {
                        type: 'table-cell',
                        children: [
                            formData?.['dealTerms.securityOffered']?.length
                                ? {
                                      type: 'unorderedList',
                                      children: formData?.['dealTerms.securityOffered']?.map((security: any) =>
                                          createListItem([{ text: `${security ?? '-'}` }]),
                                      ),
                                  }
                                : {
                                      type: 'paragraph',
                                      children: [{ text: DataNotAvl, bgColor: YELLOW }],
                                  },
                        ],
                    },
                ],
            },
            {
                type: 'table-row',
                children: [
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Deal Notes' }],
                            },
                        ],
                    },
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [
                                    formData?.['dealTerms.dealNotes']
                                        ? { text: `${formData?.['dealTerms.dealNotes'] ?? ''}` }
                                        : { text: DataNotAvl, bgColor: YELLOW },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    };
    table_preferredDealTerms.rows = table_preferredDealTerms?.children?.length;
    return table_preferredDealTerms;
};

export const updateTemplateDataWithPreferredTerms = (existingTemplateData, formData) => {
    let dataToUpdate = JSON.parse(JSON.stringify(existingTemplateData));
    // let table_preferredDealTerms = updatePreferredTermsInEditorData(formData);
    // if (table_preferredDealTerms) {
    //     dataToUpdate = [
    //         ...dataToUpdate,
    //         { type: 'headingThree', children: [{ text: 'Preferred Terms' }] },
    //         table_preferredDealTerms,
    //     ];
    // }
    let dealSummaryDataIndex = dataToUpdate.findIndex((table) => table?.key === 'table_dealSummary');
    if (dealSummaryDataIndex > -1) {
        let dealSummaryData = dataToUpdate[dealSummaryDataIndex]?.children;
        let sectorIndustryIndex = dealSummaryData?.findIndex((table) => table?.key === 'sector_industry');

        if (sectorIndustryIndex > -1) {
            dealSummaryData[sectorIndustryIndex] = {
                key: 'sector_industry',
                type: 'table-row',
                children: [
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Sector/Industry' }],
                            },
                        ],
                    },
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [
                                    formData && formData?.['borrowerProfile.companySector']
                                        ? {
                                              text: `${formData?.['borrowerProfile.companySector']}`,
                                              bold: true,
                                          }
                                        : {
                                              text: `${DataNotAvl}`,
                                              bgColor: YELLOW,
                                              bold: true,
                                          },
                                ],
                            },
                        ],
                    },
                ],
            };
        }
    }

    return dataToUpdate;
};

function formatCompanyMetricsValue(value: any, placeholder: string = '-'): string {
    if (value === null || value === undefined || value === '') {
        return placeholder;
    }
    const numericValue = parseFloat(String(value));
    if (!Number.isFinite(numericValue)) {
        return placeholder;
    }
    // Format value based on whether it has decimal digits
    const hasDecimal = numericValue % 1 !== 0;
    const absValue = Math.abs(numericValue);
    const formatted = hasDecimal ? Math.trunc(absValue * 10) / 10 : absValue.toString();
    // Format negative values with parentheses, positive values as-is
    return numericValue < 0 ? `(${formatted})` : `${formatted}`;
}

export function processAuditedFinancialUnsecuredLoanData(
    auditedFinancialUnsecuredData: { headers: any[]; rows: Array<Record<string, any>> } = { headers: [], rows: [] },
) {
    if (!auditedFinancialUnsecuredData) return null;
    let data = JSON.parse(JSON.stringify(auditedFinancialUnsecuredData));
    let headers = data?.headers ?? [];
    let rows = data?.rows ?? [];

    let debtRowToAdd = {
        classificationName: 'Unsecured Loans',
        label: 'Equity & Liabilities',
        parent: 'Debt',
        particular: 'Equity And Liabilities',
        subLabel: 'Non-current Liabilities',
    };

    rows.forEach((row: any) => {
        const keysToCollate = Object.entries(row).filter(
            ([k]) => !['classificationId', 'classificationName', 'id', 'label', 'parent', 'particular', 'subLabel'].includes(k),
        );
        keysToCollate.forEach(([key, val]) => {
            const num = Number(val);
            if (Number.isNaN(num)) return; // skip non-numeric
            // Fix floating point precision issues by rounding to 1 decimal place
            debtRowToAdd[key] = Number(((debtRowToAdd[key] ?? 0) + num).toFixed(1));
        });
    });

    data.rows.push(debtRowToAdd);
    return data;
}
